import { useMemo, useState } from 'react';
import { useTheme } from '@/contexts/ThemeContext';
import { Button } from '@/components/ui/button';
import { Calculator, TrendingDown, ArrowRight } from 'lucide-react';
import { tokens } from '../theme';
import { EXTERNAL_URLS } from '../data';

/**
 * LeakCalculator — "calculadora de fuga". Neuroventas HONESTAS: no inventa métricas de LeadCEM;
 * computa con los números que el propio dueño ajusta y muestra la cuenta transparente. El panel de
 * resultado es navy en ambos modos (acento), con texto blanco blindado (text-[#ffffff]) para que el
 * CSS global de día no lo oscurezca. Temizado con landing/theme.js (día/noche).
 */
const MXN = new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN', maximumFractionDigits: 0 });

const SLIDERS = [
  { key: 'leadsMes',     label: 'Leads que recibes al mes',        min: 10,   max: 1000,   step: 10,   fmt: (v) => `${v}` },
  { key: 'ticket',       label: 'Valor promedio de una venta',     min: 2000, max: 500000, step: 1000, fmt: (v) => MXN.format(v) },
  { key: 'sinContestar', label: '% que hoy NO contestas a tiempo', min: 5,    max: 80,     step: 5,    fmt: (v) => `${v}%` },
  { key: 'cierre',       label: 'De los bien atendidos, cierras ~', min: 5,   max: 50,     step: 5,    fmt: (v) => `${v}%` },
];

export default function LeakCalculator() {
  const { theme } = useTheme();
  const t = tokens(theme);
  const [vals, setVals] = useState({ leadsMes: 100, ticket: 25000, sinContestar: 40, cierre: 20 });

  const { fugaMes, fugaAno, leadsPerdidos } = useMemo(() => {
    const lp = vals.leadsMes * (vals.sinContestar / 100);
    const vp = lp * (vals.cierre / 100);
    const fm = vp * vals.ticket;
    return { fugaMes: fm, fugaAno: fm * 12, leadsPerdidos: Math.round(lp) };
  }, [vals]);

  const set = (k) => (e) => setVals((s) => ({ ...s, [k]: Number(e.target.value) }));

  return (
    <section id="calculadora" className={`py-20 md:py-24 ${t.sectionA}`} data-testid="leak-calculator-section">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-10">
          <p className={`text-xs md:text-sm uppercase tracking-[0.2em] font-bold ${t.accentText} inline-flex items-center gap-2 justify-center`}>
            <Calculator className="w-4 h-4" /> Calculadora de fuga
          </p>
          <h2 className={`text-2xl md:text-4xl lg:text-5xl font-semibold mt-4 ${t.text}`}>¿Cuánto dinero se te escapa por no contestar a tiempo?</h2>
          <p className={`text-sm md:text-base ${t.textSub} mt-4`}>Mueve las barras con TUS números. La cuenta es transparente: tú ves de dónde sale.</p>
        </div>

        <div className="grid lg:grid-cols-2 gap-6 lg:gap-8 items-stretch">
          {/* Controles */}
          <div className={`rounded-2xl p-6 sm:p-8 space-y-6 ${t.surface}`}>
            {SLIDERS.map((s) => (
              <div key={s.key}>
                <div className="flex items-baseline justify-between mb-2 gap-3">
                  <label htmlFor={`leak-${s.key}`} className={`text-sm ${t.textSub}`}>{s.label}</label>
                  <span className={`text-base font-semibold tabular-nums ${t.text}`} data-testid={`leak-val-${s.key}`}>{s.fmt(vals[s.key])}</span>
                </div>
                <input
                  id={`leak-${s.key}`}
                  type="range"
                  min={s.min}
                  max={s.max}
                  step={s.step}
                  value={vals[s.key]}
                  onChange={set(s.key)}
                  className="w-full cursor-pointer"
                  style={{ accentColor: '#7cc23e' }}
                  aria-label={s.label}
                />
              </div>
            ))}
          </div>

          {/* Resultado (navy en ambos modos) */}
          <div className="lc-beam rounded-2xl p-6 sm:p-8 bg-gradient-to-br from-[#0f2f5c] to-[#1c3f74] text-[#ffffff] flex flex-col justify-center relative overflow-hidden">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_85%_15%,rgba(124,194,62,.18),transparent_55%)]" />
            <div className="relative">
              <p className="text-[#ffffff]/70 text-sm inline-flex items-center gap-2">
                <TrendingDown className="w-4 h-4 text-lime-300" /> Se te está fugando aprox.
              </p>
              <p className="text-4xl sm:text-5xl lg:text-6xl font-bold mt-2 leading-none tabular-nums" data-testid="leak-result-month">
                {MXN.format(Math.round(fugaMes))}<span className="text-lg font-medium text-[#ffffff]/70"> /mes</span>
              </p>
              <p className="text-lg sm:text-xl mt-2 text-[#ffffff]/85 tabular-nums">≈ {MXN.format(Math.round(fugaAno))} al año</p>
              <p className="text-[#ffffff]/75 text-sm mt-4 border-t border-white/15 pt-4">
                Son ~<b className="text-lime-300">{leadsPerdidos} leads/mes</b> que ya pagaste y hoy se enfrían por no contestar a tiempo.
                LeadCEM los contesta en segundos, 24/7 — con que recuperes una fracción, se paga solo.
              </p>
              <a href={EXTERNAL_URLS.whatsapp} target="_blank" rel="noopener noreferrer" className="block mt-6">
                <Button className="lc-shine w-full bg-lime-400 hover:bg-lime-300 text-slate-950 rounded-full h-12 font-semibold" data-testid="leak-cta">
                  Quiero dejar de perderlos <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </a>
              <p className="text-[#ffffff]/45 text-[11px] mt-3">Estimación con los supuestos que tú ajustas arriba. No es una promesa de resultados.</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
